import { useEffect, useState } from 'react';
import { previewEqualMonthly } from '../../services/api';
import { formatCurrency } from '../../utils/helpers';

/**
 * Live preview for STRAIGHT_LINE_MONTHLY (Equal Monthly) revenue recognition.
 */
export default function EqualMonthlyPreviewPanel({
  serviceFrom,
  serviceTo,
  totalAmount,
  currency = 'INR',
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!serviceFrom || !serviceTo || !totalAmount || parseFloat(totalAmount) <= 0) {
      setData(null);
      return;
    }
    if (new Date(serviceTo) <= new Date(serviceFrom)) {
      setData(null);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await previewEqualMonthly({
          service_period_from: serviceFrom,
          service_period_to: serviceTo,
          total_amount: parseFloat(totalAmount),
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Preview failed');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [serviceFrom, serviceTo, totalAmount]);

  if (!serviceFrom || !serviceTo || !totalAmount) return null;

  return (
    <div className="space-y-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
      {loading && !data && (
        <p className="text-xs text-gray-400 animate-pulse">Calculating equal monthly schedule…</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">N (denominator months)</p>
              <p className="text-sm font-semibold text-gray-800">{data.N} month{data.N !== 1 ? 's' : ''}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Monthly rate</p>
              <p className="text-sm font-semibold text-gray-800">
                {formatCurrency(data.monthlyRate, currency)}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-1.5 pr-2 font-semibold">Month</th>
                  <th className="py-1.5 pr-2 font-semibold">Type</th>
                  <th className="py-1.5 pr-2 font-semibold text-right">Days</th>
                  <th className="py-1.5 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((row) => {
                  const dim = row.daysInPeriod / row.prorFactor;
                  return (
                    <tr key={row.periodMonth} className="border-b border-gray-100 last:border-0">
                      <td className="py-1.5 pr-2 font-medium text-gray-700">{row.periodLabel}</td>
                      <td className="py-1.5 pr-2 text-gray-600">
                        {row.isBalance ? (
                          <span
                            className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium"
                            title="This period absorbs rounding differences to ensure the schedule total equals the contract amount exactly."
                          >
                            Balance
                          </span>
                        ) : row.isPartialStart ? (
                          <span className="text-violet-700">
                            Prorated — {row.daysInPeriod} of {Math.round(dim)} days
                          </span>
                        ) : (
                          <span className="text-gray-500">Full month</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 text-right text-gray-500">{row.daysInPeriod}</td>
                      <td className="py-1.5 text-right font-semibold text-gray-800">
                        {formatCurrency(row.amount, currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
